import * as React from 'react';
import * as ReactDOM from 'react-dom';

type Props = {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  /** Gap in px between the anchor's bottom edge and the panel */
  offset?: number;
  zIndex?: number;
  /** Ref to the positioned panel wrapper, for outside-click checks */
  panelRef?: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
};

// Renders children into document.body, fixed-positioned directly below the
// anchor element and matching its width. Lets dropdown panels escape
// overflow-clipped containers such as modals. Tracks the anchor across
// scrolling (capture phase, so scrolling containers count) and resizes.
function AnchoredPanel({
  anchorRef,
  open,
  offset = 6,
  zIndex = 1100,
  panelRef,
  children
}: Props) {
  const [rect, setRect] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  React.useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const box = anchorRef.current?.getBoundingClientRect();
      if (box) {
        setRect({ top: box.bottom, left: box.left, width: box.width });
      }
    };
    update();
    window.addEventListener('resize', update);
    document.addEventListener('scroll', update, true);
    // The anchor itself can change size while open (e.g. a token input
    // gaining/losing chips) — scroll/resize listeners won't catch that
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && anchorRef.current) {
      observer = new ResizeObserver(update);
      observer.observe(anchorRef.current);
    }
    return () => {
      window.removeEventListener('resize', update);
      document.removeEventListener('scroll', update, true);
      observer?.disconnect();
    };
  }, [open, anchorRef]);

  if (!open || !rect) return null;

  return ReactDOM.createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: rect.top + offset,
        left: rect.left,
        width: rect.width,
        zIndex
      }}
    >
      {children}
    </div>,
    document.body
  );
}

export default AnchoredPanel;
